import { useQuery } from "@apollo/client";
import { useCallback } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import { LogoAws } from "components/icons/generated";
import MetaInfoListItem from "components/MetaInfoList/Item";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import SpacePath from "components/SpacePath";
import Button from "ds/components/Button";
import useAnalytics from "hooks/useAnalytics";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import { useLayoutActions } from "views/Account/Integrations/hooks/useLayoutActions";
import { useLayoutHeading } from "views/Account/Integrations/hooks/useLayoutHeading";
import { useLayoutMeta } from "views/Account/Integrations/hooks/useLayoutMeta";
import { IntegrationsOutletContext } from "views/Account/Integrations/types";

import CloudIntegrationsAwsActionsDropdown from "../components/ActionsDropdown";
import { showAwsIntegrationDetailsDrawer } from "../components/DetailsDrawer";
import { GET_AWS_INTEGRATION } from "./gql";
import IntegrationUsedBy from "./UsedBy";

const AwsIntegrationDetailsPage = () => {
  const { integrationId } = useParams<{ integrationId: string }>();
  const { integration } = useOutletContext<IntegrationsOutletContext>();
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(GET_AWS_INTEGRATION, {
    variables: { id: integrationId },
  });
  const trackAnalytics = useAnalytics();

  useTitle(`${integration.name} · ${data?.awsIntegration?.name || ""}`);

  useBreadcrumbs(
    [
      {
        title: "Integrations",
        link: "/integrations",
      },
      {
        title: integration.name,
        link: `/integrations/${integration.id}`,
      },
      {
        title: data?.awsIntegration?.name ?? "",
      },
    ],
    [data]
  );

  useLayoutHeading(data?.awsIntegration?.name ?? "");
  useLayoutMeta(
    <>
      <SpacePath
        variant="meta"
        spaceId={data?.awsIntegration?.spaceDetails.id}
        fallbackName={data?.awsIntegration?.spaceDetails.name}
      />
      <MetaInfoListItem icon={LogoAws}>AWS</MetaInfoListItem>
    </>
  );

  const handleDetails = () => {
    if (data?.awsIntegration) {
      showAwsIntegrationDetailsDrawer({ integration: data.awsIntegration, trackAnalytics });
    }
  };

  const handleDeleteSuccess = useCallback(() => {
    navigate(`/integrations/${integration.id}`, { replace: true });
  }, [navigate, integration]);

  useLayoutActions(
    <>
      <Button variant="secondary" onPress={handleDetails}>
        Details
      </Button>
      {data?.awsIntegration && (
        <CloudIntegrationsAwsActionsDropdown
          integration={data?.awsIntegration}
          trackAnalytics={trackAnalytics}
          hideSeeDetails
          onDeleteSuccess={handleDeleteSuccess}
          dotsSize="medium"
          refetchQueriesOnEdit={["GetAwsIntegration"]}
          refetchQueriesOnDelete={["SearchAWSIntegrations"]}
        />
      )}
    </>
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data) {
    return <PageLayoutSkeleton />;
  }

  if (!data?.awsIntegration) {
    return <NotFoundPage />;
  }

  return (
    <>
      <IntegrationUsedBy integration={data.awsIntegration} />
    </>
  );
};

export default AwsIntegrationDetailsPage;
